/**
 * StatsBar — the unified statistics bar (CLAUDE.md §37).
 *
 * One bar on every role dashboard, showing ONE set of statistics at a time:
 *   files     — File folders    (dashboardStats.js: computeMonthStats / computeOverdue)
 *   prep      — Prep requests   (prepRequests.js: computePrepStats + computePrepPeriodStats)
 *   referrals — Referrals       (referralStats.js: computeReferralStats)
 *   reviews   — Annual reviews  (reviewStats.js: computeReviewStats — meetings)
 *
 * Each set is computed from its own source only (§21.1). The bar never
 * combines figures from different sets — the toggle only changes which set
 * is displayed. Every set excludes test records (is_test = true, §33) at
 * source, inside its own stats module.
 *
 * Who sees what (keyed off the ACTIVE role, like the rest of the app):
 *   adviser                — four-tab toggle; the choice is saved to
 *                            user_profiles.stats_bar_view and restored on
 *                            the next visit. Falls back to File folders when
 *                            the value is missing, invalid or unreadable.
 *   assistant              — no toggle; fixed "File folders" label + tiles.
 *   new_business_assistant — no toggle; fixed "Prep requests" label + tiles.
 *
 * Attention badges (Adviser toggle only, unselected tabs, count > 0):
 *   File folders — Overdue; Prep requests — Overdue; Referrals — distinct
 *   referrals overdue and/or feedback owed; Annual reviews — Overdue.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FileText, CheckCircle2, TrendingUp, Users, AlertTriangle,
  RefreshCw, Paperclip, MessageSquareHeart, Trophy, PenLine, Clock, CalendarDays,
} from 'lucide-react';
import StatCard from './StatCard';
import { useAuth } from '../../auth/AuthProvider';
import { getStatsBarView, saveStatsBarView } from '../../api/userProfilesApi';
import { computeMonthStats, computeOverdue } from '../../utils/dashboardStats';
import { computePrepStats, computePrepPeriodStats } from '../../utils/prepRequests';
import { computeReferralStats, countReferralsNeedingAttention } from '../../utils/referralStats';
import { computeReviewStats } from '../../utils/reviewStats';
import { getCurrentSASTYearMonth } from '../../utils/reviewPlanner';

const TABS = [
  { key: 'files', label: 'File folders' },
  { key: 'prep', label: 'Prep requests' },
  { key: 'referrals', label: 'Referrals' },
  { key: 'reviews', label: 'Annual reviews' },
];

const FIXED_VIEW_BY_ROLE = {
  assistant: 'files',
  new_business_assistant: 'prep',
};

const OK = 'var(--cp-success)';
const DANGER = 'var(--cp-danger)';

export default function StatsBar({
  files = [],
  systemNotesByFile = {},
  prepRequests = [],
  referrals = [],
  reviewClients = [],
  completedReviewClients = [],
  filterMonth,
}) {
  const { role, user } = useAuth();
  const isAdviser = role === 'adviser';
  const fixedView = FIXED_VIEW_BY_ROLE[role] || null;

  // ── Adviser's saved choice ────────────────────────────────────────────────
  const [adviserView, setAdviserView] = useState('files');
  // Set once the Adviser clicks, so a slow initial read never overrides it.
  const touchedRef = useRef(false);

  useEffect(() => {
    if (!isAdviser || !user?.id) return undefined;
    let cancelled = false;
    getStatsBarView(user.id).then((saved) => {
      if (!cancelled && !touchedRef.current && saved) setAdviserView(saved);
    });
    return () => { cancelled = true; };
  }, [isAdviser, user?.id]);

  const view = isAdviser ? adviserView : fixedView;

  const chooseView = (key) => {
    if (key === adviserView) return;
    touchedRef.current = true;
    setAdviserView(key); // switch immediately; the save runs behind it
    saveStatsBarView(user?.id, key); // logs its own warning on failure
  };

  // ── Figures — each set from its own source only (§21.1) ───────────────────
  const fileStats = useMemo(
    () => (view === 'files' ? computeMonthStats(files, filterMonth, systemNotesByFile) : null),
    [view, files, filterMonth, systemNotesByFile]
  );
  const fileOverdue = useMemo(
    () => (isAdviser || view === 'files' ? computeOverdue(files, systemNotesByFile) : 0),
    [isAdviser, view, files, systemNotesByFile]
  );
  const prepStats = useMemo(
    () => (isAdviser || view === 'prep' ? computePrepStats(prepRequests, filterMonth) : null),
    [isAdviser, view, prepRequests, filterMonth]
  );
  const referralStats = useMemo(
    () => (isAdviser ? computeReferralStats(referrals) : null),
    [isAdviser, referrals]
  );
  const referralAttention = useMemo(
    () => (isAdviser ? countReferralsNeedingAttention(referrals) : 0),
    [isAdviser, referrals]
  );
  const prepPeriod = useMemo(
    () => (view === 'prep' ? computePrepPeriodStats(prepRequests, filterMonth) : null),
    [view, prepRequests, filterMonth]
  );
  // Active + completed review rows: the active set drives Due / In
  // Progress / Overdue, the completed set drives Completed This Year.
  const reviewStats = useMemo(
    () => (isAdviser
      ? computeReviewStats([...reviewClients, ...completedReviewClients], getCurrentSASTYearMonth())
      : null),
    [isAdviser, reviewClients, completedReviewClients]
  );

  if (!view) return null; // unknown role — nothing to show

  const badges = {
    files: fileOverdue,
    prep: prepStats ? prepStats.overdue : 0,
    referrals: referralAttention,
    reviews: reviewStats ? reviewStats.overdue : 0,
  };

  let tiles = [];
  if (view === 'files' && fileStats) {
    tiles = [
      { label: 'Submitted', value: fileStats.submitted, icon: FileText, color: 'var(--cp-secondary)' },
      { label: 'Completed', value: fileStats.completed, icon: CheckCircle2, color: OK },
      { label: 'Avg. Days', value: fileStats.avgDays, icon: TrendingUp, color: 'var(--cp-primary)' },
      { label: 'SLA Compliance', value: `${fileStats.slaCompliance}%`, icon: Users, color: OK },
      { label: 'Overdue', value: fileOverdue, icon: AlertTriangle, color: fileOverdue > 0 ? DANGER : OK },
      { label: 'Bonus Qualifying', value: fileStats.bonusQualifying, icon: TrendingUp, color: 'var(--cp-accent)' },
    ];
  } else if (view === 'prep' && prepStats && prepPeriod) {
    tiles = [
      { label: 'Submitted', value: prepPeriod.submitted, icon: PenLine, color: 'var(--cp-secondary)' },
      { label: 'In Progress', value: prepStats.inProgress, icon: RefreshCw, color: OK },
      { label: 'Completed', value: prepStats.completed, icon: CheckCircle2, color: OK },
      { label: 'Avg. Days', value: prepPeriod.avgDays, icon: Clock, color: 'var(--cp-primary)' },
      { label: 'Overdue', value: prepStats.overdue, icon: AlertTriangle, color: prepStats.overdue > 0 ? DANGER : OK },
    ];
  } else if (view === 'referrals' && referralStats) {
    const s = referralStats;
    tiles = [
      { label: 'Active', value: s.active, icon: Paperclip, color: 'var(--cp-referral-ink)' },
      { label: 'Overdue', value: s.overdue, icon: AlertTriangle, color: s.overdue > 0 ? DANGER : OK },
      { label: 'Feedback Owed', value: s.feedbackOwed, icon: MessageSquareHeart, color: s.feedbackOwed > 0 ? 'var(--cp-referral-orange)' : OK },
      { label: 'Converted This Year', value: s.convertedThisYear, icon: Trophy, color: 'var(--cp-referral-ink)' },
    ];
  } else if (view === 'reviews' && reviewStats) {
    const r = reviewStats;
    tiles = [
      { label: 'Due This Month', value: r.dueThisMonth, icon: CalendarDays, color: 'var(--cp-secondary)' },
      { label: 'In Progress', value: r.inProgress, icon: RefreshCw, color: OK },
      { label: 'Completed This Year', value: r.completedThisYear, icon: CheckCircle2, color: 'var(--cp-primary)' },
      { label: 'Overdue', value: r.overdue, icon: AlertTriangle, color: r.overdue > 0 ? DANGER : OK },
    ];
  }

  const activeLabel = TABS.find((t) => t.key === view)?.label || '';

  return (
    <div className="stats-bar">
      <div className="stats-bar-head">
        {isAdviser ? (
          <>
            <StatsTabs view={view} badges={badges} onChoose={chooseView} />
            <span className="stats-bar-hint">Your choice is remembered</span>
          </>
        ) : (
          <div className="stats-bar-fixed-label">{activeLabel}</div>
        )}
      </div>

      <div
        id="stats-bar-panel"
        className="stats-bar-grid"
        style={{
          '--stats-cols': tiles.length || 1,
          '--stats-cols-md': Math.min(tiles.length || 1, 3),
        }}
        {...(isAdviser
          ? { role: 'tabpanel', 'aria-labelledby': `stats-tab-${view}` }
          : { role: 'group', 'aria-label': `${activeLabel} statistics` })}
      >
        {tiles.map((t) => (
          <StatCard key={t.label} label={t.label} value={t.value} icon={t.icon} color={t.color} />
        ))}
      </div>
    </div>
  );
}

/**
 * The Adviser's four-tab segmented toggle. WAI-ARIA tabs pattern with
 * automatic activation: Left / Right (and Home / End) move the selection,
 * roving tabindex keeps a single tab stop.
 */
function StatsTabs({ view, badges, onChoose }) {
  const refs = useRef({});

  const onKeyDown = (e) => {
    const i = TABS.findIndex((t) => t.key === view);
    let next = null;
    if (e.key === 'ArrowRight') next = TABS[(i + 1) % TABS.length];
    else if (e.key === 'ArrowLeft') next = TABS[(i - 1 + TABS.length) % TABS.length];
    else if (e.key === 'Home') next = TABS[0];
    else if (e.key === 'End') next = TABS[TABS.length - 1];
    if (!next) return;
    e.preventDefault();
    onChoose(next.key);
    refs.current[next.key]?.focus();
  };

  return (
    <div className="stats-tabs" role="tablist" aria-label="Statistics set" onKeyDown={onKeyDown}>
      {TABS.map((t) => {
        const selected = t.key === view;
        const count = badges[t.key] || 0;
        return (
          <button
            key={t.key}
            ref={(el) => { refs.current[t.key] = el; }}
            id={`stats-tab-${t.key}`}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls="stats-bar-panel"
            tabIndex={selected ? 0 : -1}
            className={`stats-tab${selected ? ' is-selected' : ''}`}
            onClick={() => onChoose(t.key)}
          >
            {t.label}
            {!selected && count > 0 && (
              <span className="stats-tab-badge" role="img" aria-label={`${count} need attention`}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
